// frontend/src/errors/ErrorBoundary.tsx
import React from 'react';
import { Modal } from '../shared/components/Modal';

type LastTraceInfo = {
  url?: string;
  status?: number;
  trace_id?: string;
  date?: string;
};

type State = {
  error: Error | null;
  muted: boolean;
};

function lastTraceInfo(): LastTraceInfo | null {
  try {
    const v = sessionStorage.getItem('lastRequestInfo');
    if (!v) return null;
    const parsed = JSON.parse(v);
    return {
      url: typeof parsed?.url === 'string' ? parsed.url : undefined,
      status:
        typeof parsed?.status === 'number'
          ? parsed.status
          : Number(parsed?.status) || undefined,
      trace_id: typeof parsed?.trace_id === 'string' ? parsed.trace_id : undefined,
      date: typeof parsed?.date === 'string' ? parsed.date : undefined,
    };
  } catch {
    return null;
  }
}

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<{}>,
  State
> {
  constructor(props: any) {
    super(props);
    this.state = {
      error: null,
      muted:
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem('errorOverlayMuted') === '1',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // React will call this when a child throws
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log once, but do not setState here (we already do it in getDerivedStateFromError)
    // eslint-disable-next-line no-console
    console.error(error, info);
  }

  private handleClose = () => {
    // Dismiss this error and mute future overlays for this tab
    if (typeof window !== 'undefined') {
      window.sessionStorage?.setItem('errorOverlayMuted', '1');
    }
    this.setState({ error: null, muted: true });
  };

  render() {
    const { error, muted } = this.state;
    const info = lastTraceInfo();

    // If muted or no error, just render children
    if (!error || muted) {
      return <>{this.props.children}</>;
    }

    // If we have a status and it's < 500, treat as "expected-ish" and don't block UI
    if (info?.status && info.status < 500) {
      return <>{this.props.children}</>;
    }

    // Otherwise, show the modal with trace info
    return (
      <>
        {this.props.children}
        <Modal
          open={!!error && !muted}
          onClose={this.handleClose}
          title="Something went wrong"
        >
          <p className="text-sm">
            We hit an error. If you contact support, include the{' '}
            <code>trace_id</code> and details below.
          </p>
          <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto mt-2">
            {JSON.stringify(info, null, 2)}
          </pre>
          {info?.trace_id && (
            <a
              className="text-blue-600 underline text-sm"
              href={
                (import.meta.env.VITE_TRACE_EXPLORER || '#') +
                String(info.trace_id)
              }
              target="_blank"
              rel="noreferrer"
            >
              Open trace
            </a>
          )}
        </Modal>
      </>
    );
  }
}